import { AutomateTask, success } from "@iflyrpa/share";
import type { AxiosProxyParams } from "../../types";
import { Http } from "../../utils/http";
import { type FetchArticlesParams } from ".";
import { errorResponse, FetchArticlesResponse, ShipinhaoArticle } from "./types";

// API 响应接口定义
interface ShipinhaoApiPost {
    objectId?: string;
    exportId?: string;
    object_desc?: string;
    desc?: {
        description?: string;
        media?: Array<{
            thumbUrl?: string;
            coverUrl?: string;
            url?: string;
        }>;
    };
    cover?: {
        url: string;
    };
    media_url?: string;
    createTime?: number;
    effectiveTime?: number;
    create_time?: number;
    post_status?: number;
    status?: number;
    post_type?: number;
    objectType?: number;

    // 统计数据
    readCount?: number;
    read_count?: number;
    likeCount?: number;
    like_count?: number;
    commentCount?: number;
    comment_count?: number;
    forwardCount?: number;
    forward_count?: number;
    favCount?: number;
    fav_count?: number;
}

interface ShipinhaoPostListResponse {
    errCode?: number;
    errMsg?: string;
    data?: {
        list?: ShipinhaoApiPost[];
        totalCount?: number;
        continueFlag?: boolean;
        lastBuff?: string;
    };
    posts?: ShipinhaoApiPost[];
    post_list?: ShipinhaoApiPost[];
    has_more?: boolean;
    total?: number;
    offset?: number;
}

/**
 * 视频号作品数据采集
 *
 * 采集内容：
 * 1. 账号维度数据：关注者、昨日净增关注、昨日新增播放、昨日新增喜欢、昨日新增评论
 * 2. 作品维度数据：封面图、标题、发布时间、阅读/喜欢/评论/转发/点赞等
 *
 * 参考小红书实现模式：
 * - 内部封装 fetchPosts 函数
 * - 支持代理配置
 * - 支持重试机制
 * - 视频和图文分别获取后合并
 */
export async function handleShipinhaoData(
    _task: AutomateTask,
    params: FetchArticlesParams,
): Promise<FetchArticlesResponse> {
    try {
        const { cookies, postType = "all", pageSize = 10, showOriginalData = false } = params;
        // pageNum 为 0 或未传时按 1 处理
        const pageNum = params.pageNum || 1;

        // 从cookies中提取_aid（如果存在）
        const aidCookie = cookies.find((it) => it.name === "_aid")?.value;
        const aid = aidCookie || "9d3e0e9f-b842-498d-a273-4285d58df264";

        // 提取必需的请求头信息
        const uinCookie = cookies.find((it) => it.name === "uin" || it.name === "wxuin")?.value || "0000000000";
        const deviceIdCookie = cookies.find(
            (it) => it.name === "device_id" || it.name === "finger_print_device_id",
        )?.value;

        // 配置请求头和代理
        const headers = {
            cookie: cookies.map((it) => `${it.name}=${it.value}`).join(";"),
            referer: "https://channels.weixin.qq.com",
            origin: "https://channels.weixin.qq.com",
            "content-type": "application/json",
            "X-WECHAT-UIN": uinCookie,
            ...(deviceIdCookie ? { "finger-print-device-id": deviceIdCookie } : {}),
        };

        const args: AxiosProxyParams = [{ headers }, _task.logger, params.proxyLoc, params.localIP, params.accountId];

        const http = new Http(...args);

        // 从cookies中提取_log_finder_id（如果存在）
        const finderIdCookie =
            cookies.find((it) => it.name === "_log_finder_id" || it.name === "finder_id")?.value || "";

        // 内部函数：获取作品列表（支持分页）
        // 视频: userpageType=11, pageUrl=.../post/list
        // 图文: userpageType=10, pageUrl=.../post/finderNewLifePostList
        async function fetchPosts(
            currentPage: number,
            userpageType: number,
            pageUrl: string,
            rawKeyBuff: string = "",
        ): Promise<ShipinhaoPostListResponse> {
            return await http.api<ShipinhaoPostListResponse>(
                {
                    method: "post",
                    url: "https://channels.weixin.qq.com/micro/content/cgi-bin/mmfinderassistant-bin/post/post_list",
                    params: {
                        _aid: aid,
                        _rid: `${Date.now().toString(16)}-${Math.random().toString(36).substring(2, 9)}`,
                        _pageUrl: encodeURIComponent(pageUrl),
                    },
                    data: {
                        pageSize,
                        currentPage,
                        userpageType,
                        stickyOrder: true,
                        timestamp: Date.now().toString(),
                        _log_finder_uin: "",
                        _log_finder_id: finderIdCookie,
                        rawKeyBuff,
                        pluginSessionId: null,
                        scene: 7,
                        reqScene: 7,
                    },
                },
                { retries: 3, retryDelay: 100, timeout: 5000 },
            );
        }

        // 内部函数：循环获取所有作品（处理分页）
        // 返回全部作品及该类型的总数（totalCount，用于精确统计与完整性校验）
        async function fetchAllPosts(
            userpageType: number,
            pageUrl: string,
        ): Promise<{ posts: ShipinhaoApiPost[]; totalCount: number }> {
            const allPosts: ShipinhaoApiPost[] = [];
            let currentPage = 1;
            let rawKeyBuff = "";
            let continueFlag = true;
            let totalCount = 0;

            while (continueFlag) {
                const response = await fetchPosts(currentPage, userpageType, pageUrl, rawKeyBuff);

                // 检查认证错误
                if (response.errCode && AUTH_ERROR_CODES.includes(response.errCode)) {
                    throw new Error(`AUTH_ERROR:${response.errMsg || "登录已失效"}`);
                }

                // 其他错误
                if (response.errCode) {
                    _task.logger.warn(`视频号API错误：${response.errMsg} (${response.errCode})`);
                    throw new Error(`视频号API错误：${response.errMsg} (${response.errCode})`);
                }

                // 首页记录该类型的总数
                if (currentPage === 1) {
                    totalCount = response.data?.totalCount ?? 0;
                }

                const posts = response.data?.list || response.posts || response.post_list || [];
                allPosts.push(...posts);

                // 检查是否还有更多数据
                continueFlag = response.data?.continueFlag ?? false;
                rawKeyBuff = response.data?.lastBuff || "";

                // _task.logger.info(
                //     `获取${userpageType === 11 ? "视频" : "图文"}第${currentPage}页，本页${posts.length}条，累计${allPosts.length}条，continueFlag=${continueFlag}`,
                // );

                currentPage++;

                // 防止无限循环
                if (currentPage > 100) {
                    _task.logger.warn("分页超过100页，停止请求");
                    break;
                }
            }

            // 完整性校验：拉取总数应与接口声明的 totalCount 一致
            if (totalCount > 0 && allPosts.length !== totalCount) {
                _task.logger.warn(`视频号分页可能不完整：期望${totalCount}条，实际${allPosts.length}条`);
            }

            return { posts: allPosts, totalCount };
        }

        // 将单个作品转换为统一格式
        const mapPost = (item: ShipinhaoApiPost, forcePostType: "video" | "article"): ShipinhaoArticle => {
            // 提取封面图
            let imageUrl = "";
            if (item.desc?.media?.[0]?.thumbUrl) {
                imageUrl = item.desc.media[0].thumbUrl;
            } else if (item.desc?.media?.[0]?.coverUrl) {
                imageUrl = item.desc.media[0].coverUrl;
            } else if (item.cover?.url) {
                imageUrl = item.cover.url;
            } else if (item.media_url) {
                imageUrl = item.media_url;
            }

            // 提取标题
            const title = item.desc?.description || item.object_desc || "无标题";

            return {
                title,
                imageUrl,
                createTime: (item.createTime || item.create_time) as number,
                redirectUrl: "https://channels.weixin.qq.com/platform",

                readNum: item.readCount || item.read_count || 0,
                likeNum: item.likeCount || item.like_count || 0,
                commentNum: item.commentCount || item.comment_count || 0,
                forwardNum: item.forwardCount || item.forward_count || 0,
                favNum: item.favCount || item.fav_count || 0,

                objectId: item.exportId || item.objectId || "",
                postType: forcePostType,
                postStatus: item.status || item.post_status || 0,

                ...(item.effectiveTime ? { effectiveTime: item.effectiveTime } : {}),
                ...(showOriginalData ? { originalData: item } : {}),
            };
        };

        const allArticles: ShipinhaoArticle[] = [];
        const needVideo = postType === "all" || postType === "video";
        const needArticle = postType === "all" || postType === "article";

        // 认证相关的错误码
        const AUTH_ERROR_CODES = [300333, 300334, 300330, 300331, 300332];

        // 视频 + 图文各自的总数，相加即合并总数
        let combinedTotal = 0;

        // 获取视频作品（userpageType=11）
        if (needVideo) {
            try {
                const videoPageUrl = "https://channels.weixin.qq.com/micro/content/post/list";
                const { posts, totalCount } = await fetchAllPosts(11, videoPageUrl);
                // _task.logger.info(`视频号视频获取响应：共${posts.length}条，totalCount=${totalCount}`);

                const videoArticles = posts
                    .filter((item) => item.createTime || item.create_time)
                    .map((item) => mapPost(item, "video"));

                allArticles.push(...videoArticles);
                combinedTotal += totalCount || videoArticles.length;
            } catch (error) {
                const errMsg = error instanceof Error ? error.message : String(error);
                // 认证错误直接向上抛出
                if (errMsg.startsWith("AUTH_ERROR:")) {
                    throw error;
                }
                _task.logger.warn(`获取视频号视频失败：${errMsg}`);
            }
        }

        // 获取图文作品（userpageType=10）
        if (needArticle) {
            try {
                const articlePageUrl = "https://channels.weixin.qq.com/micro/content/post/finderNewLifePostList";
                const { posts, totalCount } = await fetchAllPosts(10, articlePageUrl);
                // _task.logger.info(`视频号图文获取响应：共${posts.length}条，totalCount=${totalCount}`);

                const articleArticles = posts
                    .filter((item) => item.createTime || item.create_time)
                    .map((item) => mapPost(item, "article"));

                allArticles.push(...articleArticles);
                combinedTotal += totalCount || articleArticles.length;
            } catch (error) {
                const errMsg = error instanceof Error ? error.message : String(error);
                // 认证错误直接向上抛出
                if (errMsg.startsWith("AUTH_ERROR:")) {
                    throw error;
                }
                _task.logger.warn(`获取视频号图文失败：${errMsg}`);
            }
        }

        // 合并后按发布时间倒序排列（最新的在前）
        allArticles.sort((a, b) => b.createTime - a.createTime);

        // 全量拉取后再按 pageNum/pageSize 切片
        const total = allArticles.length;
        const start = (pageNum - 1) * pageSize;
        const pageItems = allArticles.slice(start, start + pageSize);

        // _task.logger.info(`视频号数据获取成功，合计${total}条，第${pageNum}页返回${pageItems.length}条`);
        // _task.logger.info(`视频号数据获取成功: ${JSON.stringify(pageItems)}`)

        return success(
            {
                articleCell: pageItems,
                pagination: {
                    total,
                    nextPage: start + pageSize < total,
                    pageNum,
                    pageSize,
                },
            },
            "视频号数据获取成功",
        );
    } catch (error) {
        const errMsg = error instanceof Error ? error.message : String(error);

        // 认证错误返回 414
        if (errMsg.startsWith("AUTH_ERROR:")) {
            return errorResponse("视频号数据获取失败，请检查账号状态", 414);
        }

        return errorResponse(errMsg || "视频号数据获取失败");
    }
}
